import { ScenarioBase } from "@mutiny-group/sbo-openapi-client";
import { useQuery } from "@tanstack/react-query";

import { QueryConfig } from "@/lib/react-query";
import { ScenarioBuilderClient } from "@/pages/scenario-builder/api/client/scenarioClient";
import { useScenarioBuilderClient } from "@/pages/scenario-builder/api/hooks/useScenarioBuilderClient";
import { Scenario, ScenarioType } from "@/pages/scenario-builder/types";
import { calculateROI } from "@/shared/utils/calculateROI";

import { getScenarioStatus, isScenarioEligibleForActionPlans } from "./getScenario";
import { useDefaultNodeNameInfo } from "@/hooks/shared/useDefaultNodeName";

export const getScenariosV2 = (
  scenarioBuilderClient: ScenarioBuilderClient
): Promise<ScenarioBase[]> => {
  return scenarioBuilderClient.scenarios.getScenarios();
};

type UseScenariosV2Options = {
  config?: QueryConfig<any>;
};

export const useScenariosV2 = ({ config }: UseScenariosV2Options = {}) => {
  const scenarioBuilderClient = useScenarioBuilderClient();
  const { mapDefaultNodeName } = useDefaultNodeNameInfo();
  return useQuery({
    ...config,
    queryKey: ["scenarios-v2", scenarioBuilderClient],
    queryFn: async () => await getScenariosV2(scenarioBuilderClient),
    select: (scenarios: ScenarioBase[]) => transformScenariosV2(scenarios, mapDefaultNodeName),
  });
};

const transformScenariosV2 = (
  scenarios: ScenarioBase[],
  mapDefaultNodeName: (brand: string) => string
): Scenario[] => {
  return scenarios.map((scenario) => ({
    uuid: scenario.uuid || "",
    name: scenario.name || "",
    brand: mapDefaultNodeName(scenario.scopeBrand ?? "default"),
    spend: scenario.totalBudget || 0,
    revenue: scenario.revenue || 0,
    roi:
      scenario.revenue === 0
        ? 0
        : (calculateROI(scenario.totalBudget ?? 0, scenario.revenue ?? 0) ?? 0),
    targetNodeUUID: "",
    startDate: new Date(scenario.request.startDate || ""),
    endDate: new Date(scenario.request.endDate || ""),
    createdBy: scenario.userEmail || "",
    createdAt: new Date(scenario.createdAt || ""),
    status: getScenarioStatus(scenario),
    statusMessage: "",
    type: ScenarioType.fromString(scenario.scenarioDisplayType || ""),
    geography: scenario.request.scope?.geographies?.[0] || null,
    isEligibleForActionPlans: isScenarioEligibleForActionPlans(scenario.createdAt),
    // TODO: Deprecate scenarioCount and its usage
    scenarioCount: 0,
  }));
};
